import { Controller } from "@hotwired/stimulus"

// Generic modal controller.
//
// Usage:
//   <div data-controller="modal" data-modal-open-value="false">
//     <button data-action="click->modal#open">...</button>
//     <div data-modal-target="dialog" class="hidden">...</div>
//   </div>
//
// Open state is driven by the `open` value so it can be toggled from
// outside via custom events (e.g. sandbox requests the key modal).
//
// Keyboard modality: `aria-modal` only tells assistive tech the dialog is
// modal; the browser still lets Tab walk out of it. While open, Tab and
// Shift+Tab cycle inside the dialog, and on close focus returns to the
// element that opened it.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export default class extends Controller {
  static targets = ["dialog"]
  static values = { open: Boolean }

  connect() {
    this.onKeydown = this.onKeydown.bind(this)
    document.addEventListener("keydown", this.onKeydown)
    this.render()
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
  }

  openValueChanged() {
    this.render()
  }

  open(event) {
    if (event) event.preventDefault()
    this.opener = document.activeElement
    this.openValue = true
  }

  close(event) {
    if (event) event.preventDefault()
    this.openValue = false
  }

  // Close when clicking on the backdrop itself (not on inner content).
  clickBackdrop(event) {
    if (event.target === event.currentTarget) this.close()
  }

  onKeydown(event) {
    if (!this.openValue) return
    if (event.key === "Escape") this.close()
    if (event.key === "Tab") this.trapTab(event)
  }

  // Keep Tab / Shift+Tab inside the dialog: wrap at either end, and pull
  // focus back in if it is anywhere else (e.g. the opener behind the backdrop).
  trapTab(event) {
    const focusable = this.focusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement
    const inside = this.dialogTarget.contains(active)

    if (event.shiftKey && (active === first || !inside)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && (active === last || !inside)) {
      event.preventDefault()
      first.focus()
    }
  }

  focusableElements() {
    return Array.from(this.dialogTarget.querySelectorAll(FOCUSABLE))
      .filter((el) => el.offsetParent !== null || el === document.activeElement)
  }

  render() {
    if (!this.hasDialogTarget) return
    this.dialogTarget.classList.toggle("hidden", !this.openValue)
    if (this.openValue) {
      const focusable = this.focusableElements()[0]
      if (focusable) focusable.focus()
      document.body.classList.add("overflow-hidden")
    } else {
      document.body.classList.remove("overflow-hidden")
      if (this.opener && document.contains(this.opener)) this.opener.focus()
      this.opener = null
    }
  }
}
